import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchClaimStatuses } from 'hooks/api/useClaimStatuses';
import { useChangeClaimStatus } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import useTicketDetailsPopup from '../../../hooks/useTicketDetailsPopup';
import type { ChangeTicketClaimStatusFormValuesT } from '../ChangeTicketClaimStatusPopup';

type UseChangeTicketClaimStatusPopupT = {
  isChangingClaimStatus: boolean;
  claimStatusesOptions: SelectOptionT[];
  isLoadingClaimStatusesOptions: boolean;
  handleChangeTicketClaimStatus: (data: ChangeTicketClaimStatusFormValuesT, onClose: NoopT) => void;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useChangeTicketClaimStatusPopup = (): UseChangeTicketClaimStatusPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const { ticketDetails } = useTicketDetailsPopup();
  const dispatch = useAppDispatch();
  const claimStatusesRequestParams = {
    isEnabled: true,
    roofTopId: ticketDetails?.roofTop.id || '',
  };
  /**
   * Api hooks
   */
  const claimStatusesResult = useFetchClaimStatuses(claimStatusesRequestParams);

  const changeClaimStatusMutation = useChangeClaimStatus({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  /**
   * Variables
   */
  const claimStatusesOptions = useMemo(
    () =>
      claimStatusesResult.data
        ? claimStatusesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [claimStatusesResult.data]
  );

  /**
   * Handlers
   */
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
    },
    []
  );

  const handleChangeTicketClaimStatus = useCallback(
    (data: ChangeTicketClaimStatusFormValuesT, onClose: NoopT) => {
      changeClaimStatusMutation.mutate(data, {
        onSuccess: () => {
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: 'Claim status was changed successfully',
              snackBarSeverity: 'success',
            })
          );
          onClose();
        },
        onError: error => {
          const errorMsg =
            getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
              snackBarSeverity: 'error',
            })
          );
          onClose();
        },
      });
    },
    [changeClaimStatusMutation, dispatch]
  );

  return {
    isChangingClaimStatus: changeClaimStatusMutation.isLoading,
    claimStatusesOptions,
    isLoadingClaimStatusesOptions: claimStatusesResult.isLoading,
    handleChangeTicketClaimStatus,
    handleClosePopup,
  };
};

export default useChangeTicketClaimStatusPopup;
